import type { ReactNode } from 'react'
import { useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'

/**
 * A panel anchored to the control that opened it.
 *
 * Three of these appeared within a week — the segments on a row, the board's
 * sort and hide, the `+` past the last column — and each had written its own
 * open state and its own click-away. That is three places for the same bug, and
 * `jscpd` said so.
 *
 * **Controlled, with no uncontrolled mode.** Two of the callers hold a boolean
 * and one holds "which of my two panels is open"; a component that supported
 * both would have two ways to be in one state. The caller owns it.
 *
 * **The click-away sits behind the panel, not around it.** A backdrop wrapping
 * the panel swallows the clicks meant for the controls inside it — which is how
 * a picker ends up needing two presses for everything.
 *
 * **The panel is rendered into the document, not into the cell.** It used to be
 * absolutely positioned beside its trigger, and the segments picker lives in a
 * table cell — `Table` puts the table in an `overflow-x-auto` container, and a
 * container that scrolls on one axis clips on both. The panel was cut off at the
 * bottom of the table however much room the page had, which was reported from a
 * screenshot. A portal has no clipping ancestor at all.
 *
 * **Which means this measures rather than classes.** Where the panel goes
 * depends on where its trigger ended up and how much room is left below it, and
 * neither is knowable when the stylesheet is written. Those two numbers are put
 * on the element through a ref — not a `style` prop, and not a styling decision:
 * it is the result of a measurement, and there is no other way to carry one into
 * CSS. Everything about how the panel *looks* is still a class.
 *
 * It opens below its trigger and flips above when there is not room, clamps
 * itself inside the viewport horizontally, and is never taller than the space it
 * was put in.
 */

/** Room left around the panel, so it never sits flush against an edge. */
const EDGE = 8
/** Between the trigger and the panel. */
const GAP = 4

export function Popover({
  label,
  trigger,
  triggerClassName,
  panelClassName,
  align = 'left',
  open,
  onToggle,
  onClose,
  children,
}: {
  /** What the trigger is called to a screen reader, and its title. */
  readonly label: string
  readonly trigger: ReactNode
  /** Literal classes: assembled ones do not exist in the stylesheet. */
  readonly triggerClassName: string
  /** How wide the panel is and how it is padded. Position is not a caller's. */
  readonly panelClassName: string
  /** Which edge lines up with the trigger's. Right for a control near the edge. */
  readonly align?: 'left' | 'right'
  readonly open: boolean
  readonly onToggle: () => void
  readonly onClose: () => void
  readonly children: ReactNode
}) {
  const anchor = useRef<HTMLSpanElement>(null)
  const panel = useRef<HTMLDivElement>(null)

  /**
   * Before paint, so the panel is never seen at the wrong place first.
   *
   * Re-run on scroll and resize because the trigger moves with them — and with
   * capture, or scrolling the table the trigger sits in would leave the panel
   * behind.
   */
  useLayoutEffect(() => {
    const control = anchor.current
    const box = panel.current
    if (!open || control === null || box === null) return

    const place = () => {
      const at = control.getBoundingClientRect()
      const size = box.getBoundingClientRect()

      const below = at.bottom + GAP
      const above = at.top - size.height - GAP
      // Below unless it would run off the bottom and there is genuinely room
      // above. Flipping into a smaller space is not an improvement.
      const room = below + size.height <= window.innerHeight - EDGE
      const top = Math.max(room || above < EDGE ? below : above, EDGE)

      const wanted = align === 'right' ? at.right - size.width : at.left
      const left = Math.min(Math.max(wanted, EDGE), window.innerWidth - size.width - EDGE)

      box.style.top = `${top}px`
      box.style.left = `${Math.max(left, EDGE)}px`
      box.style.maxHeight = `${window.innerHeight - top - EDGE}px`
    }

    place()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, align])

  // The way out for anybody not using a mouse. The backdrop is the other one.
  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <span className="relative inline-flex" ref={anchor}>
      <button
        aria-label={label}
        className={triggerClassName}
        onClick={onToggle}
        title={label}
        type="button"
      >
        {trigger}
      </button>

      {open
        ? createPortal(
            <>
              <button
                aria-label="Close"
                className="fixed inset-0 z-40 cursor-default"
                onClick={onClose}
                type="button"
              />
              <div
                className={`fixed z-50 overflow-y-auto rounded-card border border-line bg-surface shadow-lg ${panelClassName}`}
                ref={panel}
              >
                {children}
              </div>
            </>,
            document.body,
          )
        : null}
    </span>
  )
}
